import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Mail, Phone, MapPin, Inbox, Home, MessageSquare, Loader2, Instagram } from "lucide-react";
import AdminInstagram from "@/pages/AdminInstagram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABEL = {
  real_estate: "Real Estate",
  ugc: "UGC / Brand",
  modeling: "Modeling",
  content_creation: "Content",
  general: "General",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function StatBlock({ label, value, hint }) {
  return (
    <div className="border border-charcoal/10 bg-ivory p-6 md:p-8" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="overline">{label}</div>
      <div className="font-serif-heading text-4xl md:text-5xl text-charcoal mt-2">{value}</div>
      {hint ? <div className="text-xs text-mute mt-1">{hint}</div> : null}
    </div>
  );
}

export default function AdminDashboard() {
  const { authed, checking, logout, authHeader } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const headers = useMemo(() => ({ headers: authHeader() }), [authHeader]);

  const load = async () => {
    setLoading(true);
    try {
      const [l, c, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, headers),
        axios.get(`${API}/admin/contacts`, headers),
        axios.get(`${API}/admin/stats`, headers),
      ]);
      setLeads(l.data || []);
      setContacts(c.data || []);
      setStats(s.data || null);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Couldn't load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-ivory text-charcoal/60 text-xs uppercase tracking-[0.22em]">Checking session…</div>;
  if (!authed) return <Navigate to="/admin/login" replace />;

  const onLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const onDelete = async (kind, id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/${kind}/${id}`, headers);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const downloadCSV = () => {
    const rows = tab === "leads"
      ? [["Created", "Name", "Email", "Phone", "Address", "Type", "Timeline", "Notes"], ...leads.map((l) => [formatDate(l.created_at), l.full_name, l.email, l.phone, l.address, l.property_type || "", l.timeline || "", (l.notes || "").replace(/\n/g, " ")])]
      : [["Created", "Name", "Email", "Topic", "Message"], ...contacts.map((c) => [formatDate(c.created_at), c.name, c.email, TOPIC_LABEL[c.topic] || c.topic, (c.message || "").replace(/\n/g, " ")])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLeads = leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [l.full_name, l.email, l.phone, l.address, l.property_type, l.timeline, l.notes].some((v) => (v || "").toLowerCase().includes(s));
  });

  const filteredContacts = contacts.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [c.name, c.email, TOPIC_LABEL[c.topic], c.message].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-ivory text-charcoal" data-testid="admin-dashboard">
      <header className="border-b border-charcoal/10 bg-ivory/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="overline">Admin</div>
            <div className="font-serif-heading text-2xl md:text-3xl text-charcoal">Stefanie&apos;s Inbox</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} disabled={loading} className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 border border-charcoal/20 px-4 py-2 hover:border-bronze hover:text-bronze transition-colors" data-testid="admin-refresh">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
            </button>
            <button onClick={onLogout} className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 bg-charcoal text-ivory px-4 py-2 hover:bg-bronze transition-colors" data-testid="admin-logout">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12" data-testid="admin-stats">
          <StatBlock label="Leads" value={stats?.leads_total ?? "—"} hint={stats ? `${stats.leads_last_7d} in last 7 days` : ""} />
          <StatBlock label="Messages" value={stats?.contacts_total ?? "—"} hint={stats ? `${stats.contacts_last_7d} in last 7 days` : ""} />
          <StatBlock label="Top Source" value="QR / Valery" hint="Home valuation referrals" />
          <StatBlock label="Status" value="Live" hint="Email delivery active" />
        </section>

        {/* Tabs + Search */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex border border-charcoal/15">
            <button
              onClick={() => setTab("leads")}
              className={`px-5 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 ${tab === "leads" ? "bg-charcoal text-ivory" : "text-charcoal hover:bg-cream"}`}
              data-testid="tab-leads"
            >
              <Home size={12} /> Home Value Leads ({leads.length})
            </button>
            <button
              onClick={() => setTab("contacts")}
              className={`px-5 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 border-l border-charcoal/15 ${tab === "contacts" ? "bg-charcoal text-ivory" : "text-charcoal hover:bg-cream"}`}
              data-testid="tab-contacts"
            >
              <MessageSquare size={12} /> Messages ({contacts.length})
            </button>
            <button
              onClick={() => setTab("instagram")}
              className={`px-5 py-3 text-xs uppercase tracking-[0.22em] flex items-center gap-2 border-l border-charcoal/15 ${tab === "instagram" ? "bg-charcoal text-ivory" : "text-charcoal hover:bg-cream"}`}
              data-testid="tab-instagram"
            >
              <Instagram size={12} /> Instagram
            </button>
          </div>
          {tab !== "instagram" && (
            <>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="line-input flex-1 min-w-[200px]"
                data-testid="admin-search"
              />
              <button onClick={downloadCSV} className="text-xs uppercase tracking-[0.22em] border border-charcoal/20 px-4 py-3 hover:border-bronze hover:text-bronze transition-colors" data-testid="admin-export">
                Export CSV
              </button>
            </>
          )}
        </div>

        {/* Tables */}
        {tab === "instagram" ? (
          <AdminInstagram />
        ) : loading ? (
          <div className="py-20 text-center text-mute text-sm flex items-center justify-center gap-2" data-testid="admin-loading">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : tab === "leads" ? (
          filteredLeads.length === 0 ? (
            <EmptyState label="No home value leads yet." note="When someone submits via your QR/valuation link, leads will appear here." />
          ) : (
            <div className="space-y-4" data-testid="admin-leads-list">
              {filteredLeads.map((l) => (
                <article key={l.id} className="border border-charcoal/10 bg-ivory p-6 md:p-8" data-testid={`lead-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-serif-heading text-2xl text-charcoal">{l.full_name}</h3>
                      <div className="overline mt-1">{formatDate(l.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a href={`mailto:${l.email}`} className="text-xs uppercase tracking-[0.22em] text-charcoal hover:text-bronze">Reply</a>
                      <button onClick={() => onDelete("leads", l.id)} className="text-mute hover:text-red-700" data-testid={`lead-delete-${l.id}`} aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-ink"><Mail size={14} className="text-bronze" /> {l.email}</div>
                    <div className="flex items-center gap-2 text-ink"><Phone size={14} className="text-bronze" /> {l.phone}</div>
                    <div className="flex items-start gap-2 text-ink sm:col-span-2"><MapPin size={14} className="text-bronze mt-1" /> {l.address}</div>
                  </div>
                  {(l.property_type || l.timeline) && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {l.property_type && <span className="text-[10px] uppercase tracking-[0.22em] border border-charcoal/15 px-3 py-1">{l.property_type}</span>}
                      {l.timeline && <span className="text-[10px] uppercase tracking-[0.22em] border border-charcoal/15 px-3 py-1">{l.timeline}</span>}
                    </div>
                  )}
                  {l.notes && <p className="mt-4 text-sm text-ink leading-relaxed border-l-2 border-bronze pl-4">{l.notes}</p>}
                </article>
              ))}
            </div>
          )
        ) : filteredContacts.length === 0 ? (
          <EmptyState label="No messages yet." note="Contact form submissions will appear here." />
        ) : (
          <div className="space-y-4" data-testid="admin-contacts-list">
            {filteredContacts.map((c) => (
              <article key={c.id} className="border border-charcoal/10 bg-ivory p-6 md:p-8" data-testid={`contact-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div>
                    <h3 className="font-serif-heading text-2xl text-charcoal">{c.name}</h3>
                    <div className="overline mt-1">{formatDate(c.created_at)} · {TOPIC_LABEL[c.topic] || c.topic}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <a href={`mailto:${c.email}`} className="text-xs uppercase tracking-[0.22em] text-charcoal hover:text-bronze">Reply</a>
                    <button onClick={() => onDelete("contacts", c.id)} className="text-mute hover:text-red-700" data-testid={`contact-delete-${c.id}`} aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-ink mb-3 flex items-center gap-2"><Mail size={14} className="text-bronze" /> {c.email}</div>
                <p className="text-ink leading-relaxed whitespace-pre-wrap border-l-2 border-bronze pl-4">{c.message}</p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function EmptyState({ label, note }) {
  return (
    <div className="border border-dashed border-charcoal/20 bg-cream/40 py-20 text-center" data-testid="admin-empty">
      <Inbox className="mx-auto text-bronze" size={32} strokeWidth={1.2} />
      <h3 className="font-serif-heading text-2xl text-charcoal mt-4">{label}</h3>
      <p className="text-mute text-sm mt-2 max-w-sm mx-auto">{note}</p>
    </div>
  );
}
